import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faChartPie,
  faTriangleExclamation,
  faLightbulb,
  faCircleCheck,
  faRotate,
  faSpinner,
  faHeartPulse,
  faCar,
  faHouse,
  faPlane,
  faSkullCrossbones,
  faUserShield,
  faUmbrella,
  faCircleExclamation,
  faArrowTrendUp,
  faStar,
  faTimes,
  faWandMagicSparkles,
  faBan,
  faListCheck,
  faFileLines,
  faGem,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import insuranceService, { InsurancePolicy } from '../../services/insuranceService';
import './InsuranceInsights.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */
interface InsuranceInsightsProps {
  policies: InsurancePolicy[];
  onRefresh: () => void;
}

/* ------------------------------------------------------------------ */
/*  Summary Modal State                                                */
/* ------------------------------------------------------------------ */
interface SummaryModalState {
  visible: boolean;
  policyId: number | null;
  data: any | null;
  loading: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const TYPE_ICONS: Record<string, IconDefinition> = {
  health: faHeartPulse,
  life: faUserShield,
  motor: faCar,
  personal_accident: faSkullCrossbones,
  critical_illness: faCircleExclamation,
  travel: faPlane,
  home_property: faHouse,
  other: faUmbrella,
};

// TYPE_LABELS is now derived from translations inside the component

const getScoreColor = (score: number) => {
  if (score >= 81) return '#059669';
  if (score >= 61) return '#10b981';
  if (score >= 41) return '#f59e0b';
  return '#ef4444';
};

const getScoreGradient = (score: number) => {
  if (score >= 81) return 'linear-gradient(135deg, #059669, #047857)';
  if (score >= 61) return 'linear-gradient(135deg, #10b981, #059669)';
  if (score >= 41) return 'linear-gradient(135deg, #f59e0b, #d97706)';
  return 'linear-gradient(135deg, #ef4444, #dc2626)';
};

// getScoreLabel is now inside the component to access t()

const formatCurrency = (amount: number | string | undefined | null): string => {
  if (amount === null || amount === undefined) return '--';
  // If it's already a formatted string (e.g., "Rs.5,00,000" or "N/A"), display as-is
  if (typeof amount === 'string') {
    if (amount === '0' || amount === '') return '--';
    // Already formatted by backend
    if (amount.startsWith('Rs') || amount.startsWith('₹') || amount === 'N/A') return amount;
    const num = parseFloat(amount.replace(/[^0-9.-]/g, ''));
    if (isNaN(num) || num === 0) return amount || '--';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
  }
  if (amount === 0) return '--';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'high': return '#ef4444';
    case 'medium': return '#f59e0b';
    case 'low': return '#10b981';
    default: return '#64748b';
  }
};

// getStatusConfig is now inside the component to access t()

/* ================================================================== */
/*  InsuranceInsights Component                                        */
/* ================================================================== */
const InsuranceInsights: React.FC<InsuranceInsightsProps> = ({ policies, onRefresh }) => {
  const { t } = useTranslation(['insurance', 'common']);

  const TYPE_LABELS: Record<string, string> = {
    health: t('insurance:insights.typeLabels.health'),
    life: t('insurance:insights.typeLabels.life'),
    motor: t('insurance:insights.typeLabels.motor'),
    personal_accident: t('insurance:insights.typeLabels.personal_accident'),
    critical_illness: t('insurance:insights.typeLabels.critical_illness'),
    travel: t('insurance:insights.typeLabels.travel'),
    home_property: t('insurance:insights.typeLabels.home_property'),
    other: t('insurance:insights.typeLabels.other'),
  };

  const getScoreLabel = (score: number) => {
    if (score >= 81) return t('insurance:insights.excellent');
    if (score >= 61) return t('insurance:insights.good');
    if (score >= 41) return t('insurance:insights.needsImprovement');
    return t('insurance:insights.atRisk');
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'adequate':
        return { label: t('insurance:insights.adequate'), className: 'ii-status-adequate', icon: faCircleCheck };
      case 'underinsured':
        return { label: t('insurance:insights.underinsured'), className: 'ii-status-underinsured', icon: faTriangleExclamation };
      case 'not_covered':
      case 'not covered':
        return { label: t('insurance:insights.notCovered'), className: 'ii-status-not-covered', icon: faBan };
      default:
        return { label: status || 'Unknown', className: 'ii-status-unknown', icon: faCircleExclamation };
    }
  };

  const [insights, setInsights] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryModal, setSummaryModal] = useState<SummaryModalState>({
    visible: false,
    policyId: null,
    data: null,
    loading: false,
  });

  /* ---------- Fetch insights ---------- */
  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await insuranceService.getInsights();
      setInsights(data?.data || data);
    } catch (err: any) {
      console.error('Error fetching insurance insights:', err);
      setError(t('insurance:messages.insightsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  /* ---------- Summary modal helpers ---------- */
  const openSummaryModal = async (policyId: number) => {
    setSummaryModal({ visible: true, policyId, data: null, loading: true });
    try {
      // Try cached first — 404 is expected if no summary exists yet
      const cached = await insuranceService.getSummary(policyId);
      if (cached?.summary_text) {
        setSummaryModal(prev => ({ ...prev, data: cached, loading: false }));
        return;
      }
    } catch (e: any) {
      if (e?.response?.status !== 404) console.warn('Summary fetch error:', e);
      // 404 = no cached summary, will generate below
    }
    try {
      const generated = await insuranceService.generateSummary(policyId);
      setSummaryModal(prev => ({ ...prev, data: generated?.data || generated, loading: false }));
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummaryModal(prev => ({ ...prev, data: null, loading: false }));
    }
  };

  const regenerateSummary = async () => {
    if (!summaryModal.policyId) return;
    setSummaryModal(prev => ({ ...prev, loading: true }));
    try {
      const generated = await insuranceService.generateSummary(summaryModal.policyId);
      setSummaryModal(prev => ({ ...prev, data: generated?.data || generated, loading: false }));
    } catch (err) {
      console.error('Error regenerating summary:', err);
      setSummaryModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeSummaryModal = () => {
    setSummaryModal({ visible: false, policyId: null, data: null, loading: false });
  };

  /* ---------- Derived data ---------- */
  const overallScore = insights?.overall_score ?? 0;
  const totalPolicies = insights?.total_policies ?? policies.length;
  const totalCoverage = insights?.total_coverage ?? 0;
  const coverageMatrix = insights?.coverage_matrix ?? [];
  const gaps = insights?.gaps ?? [];
  const recommendations = insights?.recommendations ?? [];
  const strengths = insights?.strengths ?? [];

  /* ================================================================ */
  /*  LOADING STATE                                                    */
  /* ================================================================ */
  if (loading) {
    return (
      <div className="ii-container">
        <div className="ii-loading">
          <div className="ii-loading-spinner" />
          <p>{t('insurance:insights.analyzingCoverage')}</p>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  ERROR STATE                                                      */
  /* ================================================================ */
  if (error) {
    return (
      <div className="ii-container">
        <div className="ii-error">
          <FontAwesomeIcon icon={faTriangleExclamation} className="ii-error-icon" />
          <p>{error}</p>
          <button className="ii-btn ii-btn-primary" onClick={fetchInsights}>
            <FontAwesomeIcon icon={faRotate} /> {t('insurance:insights.retry')}
          </button>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  EMPTY STATE                                                      */
  /* ================================================================ */
  if (!insights) {
    return (
      <div className="ii-container">
        <div className="ii-empty">
          <FontAwesomeIcon icon={faChartPie} className="ii-empty-icon" />
          <h3>{t('insurance:insights.noInsightsAvailable')}</h3>
          <p>{t('insurance:insights.noInsightsDescription')}</p>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  MAIN RENDER                                                      */
  /* ================================================================ */
  return (
    <div className="ii-container">

      {/* ---------- Header with refresh ---------- */}
      <div className="ii-header">
        <div>
          <h2 className="ii-title">
            <FontAwesomeIcon icon={faChartPie} className="ii-title-icon" />
            {t('insurance:insights.coverageInsights')}
          </h2>
          <p className="ii-subtitle">{t('insurance:insights.subtitle')}</p>
        </div>
        <button className="ii-btn ii-btn-outline" onClick={fetchInsights}>
          <FontAwesomeIcon icon={faRotate} /> {t('insurance:insights.refresh')}
        </button>
      </div>

      {/* ========================================= */}
      {/*  1. OVERALL SCORE SECTION                 */}
      {/* ========================================= */}
      <div className="ii-score-section">
        <div className="ii-score-card">
          <div className="ii-score-circle" style={{ background: getScoreGradient(overallScore) }}>
            <span className="ii-score-number">{overallScore}</span>
            <span className="ii-score-max">/100</span>
          </div>
          <div className="ii-score-info">
            <h3 style={{ color: getScoreColor(overallScore) }}>{getScoreLabel(overallScore)}</h3>
            <p className="ii-score-label">{t('insurance:insights.coverageScore')}</p>
            <div className="ii-score-meta">
              <span className="ii-score-meta-item">
                <FontAwesomeIcon icon={faShieldHalved} />
                {totalPolicies} {totalPolicies === 1 ? t('insurance:insights.policy') : t('insurance:insights.policies')}
              </span>
              <span className="ii-score-meta-item">
                <FontAwesomeIcon icon={faGem} />
                {formatCurrency(totalCoverage)} {t('insurance:insights.totalCoverage')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================= */}
      {/*  2. COVERAGE MATRIX                       */}
      {/* ========================================= */}
      {coverageMatrix.length > 0 && (
        <div className="ii-section">
          <h3 className="ii-section-title">
            <FontAwesomeIcon icon={faShieldHalved} className="ii-section-icon" />
            {t('insurance:insights.coverageMatrix')}
          </h3>
          <div className="ii-matrix-grid">
            {coverageMatrix.map((item: any, idx: number) => {
              const statusCfg = getStatusConfig(item.status);
              const icon = TYPE_ICONS[item.type] || faUmbrella;
              const label = TYPE_LABELS[item.type] || item.type_label || item.type;
              const currentStr = item.current_coverage ?? '--';
              const recommendedStr = item.recommended_coverage ?? '--';
              const gapStr = item.gap ?? '--';
              // Parse numeric values for progress bar (strip "Rs.", commas)
              const parseAmount = (v: any) => { if (typeof v === 'number') return v; const n = parseFloat(String(v).replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
              const currentNum = parseAmount(currentStr);
              const recommendedNum = parseAmount(recommendedStr);
              const ratio = recommendedNum > 0 ? Math.min((currentNum / recommendedNum) * 100, 100) : (item.status === 'adequate' ? 100 : 0);

              return (
                <div className="ii-matrix-card" key={idx} style={{ animationDelay: `${idx * 0.07}s` }}>
                  <div className="ii-matrix-card-header">
                    <div className="ii-matrix-type">
                      <span className="ii-matrix-icon-wrap" style={{ color: getScoreColor(ratio) }}>
                        <FontAwesomeIcon icon={icon} />
                      </span>
                      <span className="ii-matrix-label">{label}</span>
                    </div>
                    <span className={`ii-status-badge ${statusCfg.className}`}>
                      <FontAwesomeIcon icon={statusCfg.icon} />
                      {statusCfg.label}
                    </span>
                  </div>

                  <div className="ii-matrix-amounts">
                    <div className="ii-matrix-amount-row">
                      <span className="ii-matrix-amount-label">{t('insurance:insights.current')}</span>
                      <span className="ii-matrix-amount-value">{formatCurrency(currentStr)}</span>
                    </div>
                    <div className="ii-matrix-amount-row">
                      <span className="ii-matrix-amount-label">{t('insurance:insights.recommended')}</span>
                      <span className="ii-matrix-amount-value ii-matrix-recommended">{formatCurrency(recommendedStr)}</span>
                    </div>
                    {gapStr && gapStr !== '--' && gapStr !== 'Rs.0' && parseAmount(gapStr) > 0 && (
                      <div className="ii-matrix-amount-row ii-matrix-gap-row">
                        <span className="ii-matrix-amount-label">{t('insurance:insights.gap')}</span>
                        <span className="ii-matrix-amount-value ii-matrix-gap">{formatCurrency(gapStr)}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="ii-matrix-progress-track">
                    <div
                      className="ii-matrix-progress-fill"
                      style={{
                        width: `${ratio}%`,
                        background: getScoreGradient(ratio),
                      }}
                    />
                  </div>
                  <span className="ii-matrix-ratio">{t('insurance:insights.percentCovered', { percent: Math.round(ratio) })}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/*  3. GAPS SECTION                          */}
      {/* ========================================= */}
      {gaps.length > 0 && (
        <div className="ii-section">
          <h3 className="ii-section-title ii-section-title-warning">
            <FontAwesomeIcon icon={faTriangleExclamation} className="ii-section-icon" />
            {t('insurance:insights.coverageGaps')}
          </h3>
          <div className="ii-gaps-grid">
            {gaps.map((gap: any, idx: number) => (
              <div className="ii-gap-card" key={idx} style={{ animationDelay: `${idx * 0.07}s` }}>
                <div className="ii-gap-icon-wrap">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                <div className="ii-gap-content">
                  <h4 className="ii-gap-title">{typeof gap === 'string' ? gap : (gap.title || gap.type || t('insurance:insights.gapIdentified'))}</h4>
                  {typeof gap !== 'string' && gap.description && <p className="ii-gap-description">{gap.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/*  4. RECOMMENDATIONS SECTION               */}
      {/* ========================================= */}
      {recommendations.length > 0 && (
        <div className="ii-section">
          <h3 className="ii-section-title">
            <FontAwesomeIcon icon={faLightbulb} className="ii-section-icon" />
            {t('insurance:insights.aiRecommendations')}
          </h3>
          <div className="ii-reco-grid">
            {recommendations.map((rec: any, idx: number) => {
              const priorityColor = getPriorityColor(rec.priority);
              return (
                <div
                  className="ii-reco-card"
                  key={idx}
                  style={{ borderLeftColor: priorityColor, animationDelay: `${idx * 0.07}s` }}
                >
                  <div className="ii-reco-header">
                    <span className="ii-reco-priority" style={{ background: `${priorityColor}14`, color: priorityColor }}>
                      {rec.priority?.toUpperCase() || 'INFO'}
                    </span>
                    {rec.insurance_type && (
                      <span className="ii-reco-type-badge">
                        <FontAwesomeIcon icon={TYPE_ICONS[rec.insurance_type] || faUmbrella} />
                        {TYPE_LABELS[rec.insurance_type] || rec.insurance_type}
                      </span>
                    )}
                  </div>
                  <h4 className="ii-reco-title">{rec.title}</h4>
                  <p className="ii-reco-reason">{rec.reason || rec.description}</p>
                  {rec.suggested_amount && (
                    <div className="ii-reco-amount">
                      <FontAwesomeIcon icon={faArrowTrendUp} />
                      {t('insurance:insights.suggested', { amount: formatCurrency(rec.suggested_amount) })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/*  5. STRENGTHS SECTION                     */}
      {/* ========================================= */}
      {strengths.length > 0 && (
        <div className="ii-section">
          <h3 className="ii-section-title ii-section-title-success">
            <FontAwesomeIcon icon={faStar} className="ii-section-icon" />
            {t('insurance:insights.whatYouAreDoingWell')}
          </h3>
          <div className="ii-strengths-grid">
            {strengths.map((item: any, idx: number) => (
              <div className="ii-strength-card" key={idx} style={{ animationDelay: `${idx * 0.07}s` }}>
                <FontAwesomeIcon icon={faCircleCheck} className="ii-strength-icon" />
                <span className="ii-strength-text">{typeof item === 'string' ? item : item.description || item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/*  Policy Summary Cards (View Summary btns) */}
      {/* ========================================= */}
      {policies.length > 0 && (
        <div className="ii-section">
          <h3 className="ii-section-title">
            <FontAwesomeIcon icon={faFileLines} className="ii-section-icon" />
            {t('insurance:insights.policySummaries')}
          </h3>
          <div className="ii-policy-summary-grid">
            {policies.map((policy) => (
              <div className="ii-policy-summary-card" key={policy.id}>
                <div className="ii-policy-summary-info">
                  <span className="ii-policy-summary-type">
                    <FontAwesomeIcon icon={TYPE_ICONS[policy.type_of_insurance] || faUmbrella} />
                    {TYPE_LABELS[policy.type_of_insurance] || policy.type_of_insurance}
                  </span>
                  <h4>{policy.policy_name || policy.insurance_company}</h4>
                  <p className="ii-policy-summary-number">{policy.policy_number}</p>
                </div>
                <button
                  className="ii-btn ii-btn-sm"
                  onClick={() => openSummaryModal(policy.id)}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('insurance:insights.viewSummary')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/*  SUMMARY MODAL                            */}
      {/* ========================================= */}
      {summaryModal.visible && (
        <div className="ii-modal-overlay" onClick={closeSummaryModal}>
          <div className="ii-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ii-modal-close" onClick={closeSummaryModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <h3 className="ii-modal-title">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              {t('insurance:insights.aiPolicySummary')}
            </h3>

            {summaryModal.loading ? (
              <div className="ii-modal-loading">
                <FontAwesomeIcon icon={faSpinner} spin className="ii-modal-spinner" />
                <p>{t('insurance:insights.generatingAiSummary')}</p>
                <span className="ii-modal-loading-hint">{t('insurance:insights.generatingHint')}</span>
              </div>
            ) : summaryModal.data ? (
              <div className="ii-modal-body">
                {/* Key highlights */}
                {summaryModal.data.key_highlights && summaryModal.data.key_highlights.length > 0 && (
                  <div className="ii-modal-section">
                    <h4 className="ii-modal-section-title">{t('insurance:insights.keyHighlights')}</h4>
                    <div className="ii-modal-pills">
                      {summaryModal.data.key_highlights.map((h: any, i: number) => (
                        <span className="ii-modal-pill" key={i}>
                          {typeof h === 'string' ? h : `${h.label}: ${h.value}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary text */}
                {summaryModal.data.summary_text && (
                  <div className="ii-modal-section">
                    <h4 className="ii-modal-section-title">{t('insurance:insights.summary')}</h4>
                    <p className="ii-modal-text">{summaryModal.data.summary_text}</p>
                  </div>
                )}

                {/* Coverage score */}
                {summaryModal.data.coverage_score != null && (
                  <div className="ii-modal-section">
                    <h4 className="ii-modal-section-title">{t('insurance:insights.coverageScore')}</h4>
                    <div className="ii-modal-score-bar-wrap">
                      <div className="ii-modal-score-bar-track">
                        <div
                          className="ii-modal-score-bar-fill"
                          style={{
                            width: `${summaryModal.data.coverage_score}%`,
                            background: getScoreGradient(summaryModal.data.coverage_score),
                          }}
                        />
                      </div>
                      <span
                        className="ii-modal-score-value"
                        style={{ color: getScoreColor(summaryModal.data.coverage_score) }}
                      >
                        {summaryModal.data.coverage_score}/100
                      </span>
                    </div>
                  </div>
                )}

                {/* Exclusions */}
                {summaryModal.data.exclusions && summaryModal.data.exclusions.length > 0 && (
                  <div className="ii-modal-section">
                    <h4 className="ii-modal-section-title ii-modal-title-warning">{t('insurance:insights.exclusions')}</h4>
                    <ul className="ii-modal-exclusions">
                      {summaryModal.data.exclusions.map((ex: string, i: number) => (
                        <li key={i}>
                          <FontAwesomeIcon icon={faBan} className="ii-modal-excl-icon" />
                          {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Claim process */}
                {summaryModal.data.claim_process && (
                  <div className="ii-modal-section">
                    <h4 className="ii-modal-section-title">{t('insurance:insights.claimProcess')}</h4>
                    <p className="ii-modal-text">{summaryModal.data.claim_process}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="ii-modal-actions">
                  <button className="ii-btn ii-btn-outline" onClick={regenerateSummary}>
                    <FontAwesomeIcon icon={faRotate} /> {t('insurance:insights.regenerate')}
                  </button>
                  <button className="ii-btn ii-btn-primary" onClick={closeSummaryModal}>
                    {t('insurance:insights.close')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ii-modal-empty">
                <FontAwesomeIcon icon={faCircleExclamation} className="ii-modal-empty-icon" />
                <p>{t('insurance:messages.summaryGenerateFailed')}</p>
                <button className="ii-btn ii-btn-primary" onClick={regenerateSummary}>
                  <FontAwesomeIcon icon={faRotate} /> {t('insurance:insights.retry')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceInsights;
